import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, IconButton, List, ListItem, ListItemAvatar, Avatar, ListItemText, Typography, Stack, Chip, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { request } from '../../Utils/request';
import BottomModal from '../../Components/BottomModel';

type Coordinate = { lat: number; lng: number };

type Place = {
  _id: string;
  name: string;
  location: { type: 'Point'; coordinates: [number, number] };
  files?: { fileKey: string; fileType: string; featured?: boolean }[];
  description?: string;
  categories?: string[];
};

const APP_BAR_HEIGHT = 56;
const containerStyle = { width: '100%', height: `calc(100vh - ${APP_BAR_HEIGHT}px)` } as const;

const getLatLng = (place: Place): Coordinate => ({
  lat: place.location.coordinates[1],
  lng: place.location.coordinates[0],
});

interface VisitedPlacesProps {
  places?: Place[];
  onBack?: () => void;
}

const VisitedPlaces: React.FC<VisitedPlacesProps> = ({ places, onBack }) => {
  const fallbackPlaces: Place[] = useMemo(
    () =>
      places ?? [
        {
          _id: '688a2ed2ab13852f196fb194',
          name: 'kanyakumari place',
          location: {
            type: 'Point',
            coordinates: [87.33113527203479, 22.3540704071579],
          },
          files: [
            {
              fileKey: '1fb657b0-edd5-45e2-acfe-b6a67187aba5.jpg',
              fileType: 'image/jpeg',
              featured: true,
            },
          ],
          description:
            'kanyakumari placekanyakumari placekanyakumari placekanyakumari placekanyakumari placekanyakumari placekanyakumari place',
          categories: ['6887d7a38a90923cb1931cf9', '687fc568d6673211d7244976'],
        },
        {
          _id: '6887d9178a90923cb1931d0b',
          name: 'Laksmamaya layout',
          location: {
            type: 'Point',
            coordinates: [77.65481599256412, 13.013825385719871],
          },
          files: [
            {
              fileKey: 'f55eeee2-1a7d-4c0a-9706-6b0488615c3e.webp',
              fileType: 'image/webp',
              featured: true,
            },
          ],
          description: 'banaswadi location flat for sale',
          categories: ['6887d7b38a90923cb1931cfd'],
        },
      ],
    [places]
  );
  const [data, setData] = useState<Place[]>(fallbackPlaces);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(fallbackPlaces[0]?._id ?? null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places'],
  });

  const listForView = useMemo(() => (data.length ? data : fallbackPlaces), [data, fallbackPlaces]);
  const center: Coordinate = useMemo(() => {
    if (!listForView.length) return { lat: 20.5937, lng: 78.9629 };
    return getLatLng(listForView[0]);
  }, [listForView]);
  const shouldFitAll = listForView.length >= 2;

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const res = await request<{ data: Place[] }>(`/profile/visited-places`, {
          method: 'GET',
          authorization: true,
        });
        if (isMounted && Array.isArray(res?.data)) {
          setData(res.data);
          if (res.data[0]?._id) setActiveId(res.data[0]._id);
        }
      } catch (e: any) {
        if (isMounted) setError(e?.message || 'Failed to load visited places');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fit bounds when data changes and map is ready
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const list = listForView;
    if (list.length >= 2) {
      const bounds = new google.maps.LatLngBounds();
      list.forEach((p) => bounds.extend(getLatLng(p)));
      mapRef.current.fitBounds(bounds, 48);
    } else if (list.length === 1) {
      const only = getLatLng(list[0]);
      mapRef.current.setCenter(only);
      mapRef.current.setZoom(14);
    }
  }, [isLoaded, listForView]);

  const handleSelect = (place: Place) => {
    setActiveId(place._id);
    const position = getLatLng(place);
    if (mapRef.current) {
      mapRef.current.panTo(position);
      mapRef.current.setZoom(14);
    }
  };

  if (loadError) return <Typography>Failed to load map</Typography>;
  if (!isLoaded) return <Typography>Loading map...</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, height: `${APP_BAR_HEIGHT}px`, alignItems: 'center' }}>
        <IconButton size="small" onClick={onBack} aria-label="back">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700}>Visited places</Typography>
      </Stack>
      <Divider />

      <Box sx={{ position: 'relative' }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={shouldFitAll ? undefined : center}
          zoom={shouldFitAll ? undefined : 10}
          options={{ disableDefaultUI: true, zoomControl: true }}
          onLoad={(m) => {
            mapRef.current = m;
            // Initial fit
            const list = listForView;
            if (list.length >= 2) {
              const bounds = new google.maps.LatLngBounds();
              list.forEach((p) => bounds.extend(getLatLng(p)));
              m.fitBounds(bounds, 48);
            } else if (list.length === 1) {
              const only = getLatLng(list[0]);
              m.setCenter(only);
              m.setZoom(14);
            }
          }}
        >
          {(data.length ? data : fallbackPlaces).map((p) => (
            <Marker
              key={p._id}
              position={getLatLng(p)}
              onClick={() => handleSelect(p)}
              icon={activeId === p._id ? undefined : undefined}
            />
          ))}
        </GoogleMap>

        <Button
          variant="contained"
          size="small"
          onClick={() => setDrawerOpen(true)}
          sx={{ position: 'absolute', bottom: 16, left: 16, borderRadius: 999, textTransform: 'none' }}
        >
          Places
        </Button>
      </Box>

      <BottomModal
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Visited places"
        maxHeight="70vh"
        minHeight="240px"
        contentProps={{ sx: { p: 0 } }}
        showDragIndicator
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
            {(data.length ? data : fallbackPlaces).map((p) => {
              const isActive = activeId === p._id;
              const imgKey = p.files?.[0]?.fileKey;
              const thumbSrc = imgKey ? `/api/files/${imgKey}` : undefined;
              return (
                <ListItem
                  key={p._id}
                  alignItems="flex-start"
                  onClick={() => handleSelect(p)}
                  sx={{ cursor: 'pointer', px: 2 }}
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" src={thumbSrc} sx={{ width: 64, height: 64 }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mx: 2 }}>
                        <Typography fontWeight={700}>{p.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          13.5 km
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.5} mt={0.5} mx={2}>
                        <Stack direction="row" spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            Eateries
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            View
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Point
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Adventure
                          </Typography>
                        </Stack>
                      </Stack>
                    }
                  />
                  <Chip icon={<StarIcon />} label="4" color={isActive ? 'success' : 'default'} size="small" sx={{ ml: 1 }} />
                </ListItem>
              );
            })}
          </List>
          {loading && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="body2">Loading visited places...</Typography>
            </Box>
          )}
          {error && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </BottomModal>
    </Box>
  );
};

export default VisitedPlaces;


